import React, { useState } from 'react';
import { Container, Row, Col, Card, Accordion } from 'react-bootstrap';
import {
  FaGraduationCap,
  FaBrain,
  FaCogs,
  FaShieldAlt,
  FaUsers,
  FaCubes,
  FaRocket,
  FaNewspaper,
  FaChartArea,
  FaList,
  FaTasks,
  FaCheckCircle,
  FaLightbulb,
  FaChartLine,
  FaBullseye,
  FaBolt,
  FaTools,
  FaPalette,
  FaMicroscope,
  FaLaptopCode,
  FaRobot,
  FaCog,
  FaDatabase,
  FaNetworkWired,
  FaCode,
  FaBookOpen,
  FaFlask
} from 'react-icons/fa';
import './BootcampCurriculum.css';

const BootcampCurriculum = ({ bootcampData, nextSessionDate, getWeekDate }) => {
  const [activeKey, setActiveKey] = useState(null);

  // Helper function to parse topics from HTML description
  const parseTopicsFromDescription = (description) => {
    if (!description) return [];

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(description, 'text/html');

      // First try to get list items from <ul><li> structure
      const listItems = Array.from(doc.querySelectorAll('li'));
      if (listItems.length > 0) {
        return listItems
          .map(li => li.textContent.trim())
          .filter(text => text && text.length > 0);
      }

      // If no list items, fall back to paragraphs
      const paragraphs = Array.from(doc.querySelectorAll('p'));
      return paragraphs
        .map(p => p.textContent.trim())
        .filter(text => text && !text.toLowerCase().includes('homework:'))
        .filter(text => text.length > 0);
    } catch (error) {
      console.error('Error parsing topics from description:', error);
      return [];
    }
  };

  // Helper function to map icon strings to React components
  const getIconComponent = (iconString) => {
    if (!iconString) {
      return <FaCubes />; // Default fallback
    }

    // If it's an emoji, return it as-is wrapped in a span
    if (/^[\u{1F000}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{1F900}-\u{1F9FF}]/u.test(iconString)) {
      return <span style={{ fontSize: '1.5em' }}>{iconString}</span>;
    }

    // Handle React component strings like "<FaCubes />" or "FaCubes"
    let iconName = iconString;

    // Extract component name from React component strings
    if (iconString.includes('<') && iconString.includes('/>')) {
      const match = iconString.match(/<(Fa\w+)\s*\/>/);
      if (match) {
        iconName = match[1];
      }
    }

    // Map FontAwesome component names to actual components
    const iconMap = {
      // Component names (from DB)
      'FaCubes': <FaCubes />,
      'FaRocket': <FaRocket />,
      'FaNewspaper': <FaNewspaper />,
      'FaChartArea': <FaChartArea />,
      'FaBrain': <FaBrain />,
      'FaCogs': <FaCogs />,
      'FaGraduationCap': <FaGraduationCap />,
      'FaShieldAlt': <FaShieldAlt />,
      'FaUsers': <FaUsers />,
      'FaList': <FaList />,
      'FaTasks': <FaTasks />,
      'FaCheckCircle': <FaCheckCircle />,
      'FaLightbulb': <FaLightbulb />,
      'FaChartLine': <FaChartLine />,
      'FaBullseye': <FaBullseye />,
      'FaBolt': <FaBolt />,
      'FaTools': <FaTools />,
      'FaPalette': <FaPalette />,
      'FaMicroscope': <FaMicroscope />,
      'FaLaptopCode': <FaLaptopCode />,
      'FaRobot': <FaRobot />,
      'FaCog': <FaCog />,
      'FaDatabase': <FaDatabase />,
      'FaNetworkWired': <FaNetworkWired />,
      'FaCode': <FaCode />,
      'FaBookOpen': <FaBookOpen />,
      'FaFlask': <FaFlask />,

      // Simple string keys (legacy support)
      'cubes': <FaCubes />,
      'rocket': <FaRocket />,
      'newspaper': <FaNewspaper />,
      'chart-area': <FaChartArea />,
      'brain': <FaBrain />,
      'cogs': <FaCogs />,
      'graduation-cap': <FaGraduationCap />,
      'shield-alt': <FaShieldAlt />,
      'users': <FaUsers />,
      'list': <FaList />,
      'tasks': <FaTasks />,
      'check-circle': <FaCheckCircle />
    };

    return iconMap[iconName] || <FaCubes />; // Fallback to default icon
  };

  // Transform session data to curriculum format
  const transformSessionsToCurriculum = (sessions) => {
    if (!sessions || sessions.length === 0) return [];

    const defaultColors = ['#ff6b00', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];

    return sessions.map((session, index) => {
      // Use the full title exactly as stored in the database
      const weekTitle = session.title || `Session ${index + 1}`;

      // Parse topics from description HTML
      const topics = parseTopicsFromDescription(session.description);

      // Use session date or calculate from getWeekDate
      const weekDate = session.scheduledAt ?
        new Date(session.scheduledAt).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric'
        }) :
        (getWeekDate ? getWeekDate(index + 1) : `Week ${index + 1} TBD`);

      return {
        week: index + 1,
        title: weekTitle,
        date: weekDate,
        description: session.subtitle || 'Master essential trading concepts and hands-on implementation.',
        topics: topics,
        homework: session.homeworkAssignment || null,
        icon: <FaCubes />, // Use consistent cubes icon for all weeks to match production
        color: session.color || defaultColors[index % defaultColors.length]
      };
    });
  };

  // Fallback curriculum when no API data available
  // This ensures the sales page always shows the bootcamp structure
  const fallbackCurriculum = [
    {
      week: 1,
      title: 'Week 1: Getting Started with AI Trading',
      date: 'Week 1',
      description: 'Introduction to AI-powered trading and setting up your environment',
      topics: [
        'Introduction to BotSpot platform and AI Bot Builder',
        'Setting up your development environment (VS Code, Cursor)',
        'Understanding market data and trading basics',
        'Your first AI-generated trading strategy',
        'Live demo: Building a simple momentum strategy'
      ],
      homework: 'Create your first AI-generated strategy using natural language prompts',
      icon: <FaCubes />,
      color: '#ff6b00'
    },
    {
      week: 2,
      title: 'Week 2: Stock Trading Strategies',
      date: 'Week 2',
      description: 'Building and backtesting stock trading algorithms',
      topics: [
        'Understanding stock market mechanics',
        'Technical indicators and their implementation',
        'Building mean reversion strategies',
        'Backtesting best practices',
        'Risk management fundamentals'
      ],
      homework: 'Build and backtest a stock trading strategy with at least 2 technical indicators',
      icon: <FaCubes />,
      color: '#3b82f6'
    },
    {
      week: 3,
      title: 'Week 3: Crypto Trading Bots',
      date: 'Week 3',
      description: '24/7 cryptocurrency trading automation',
      topics: [
        'Crypto market structure and exchanges',
        'Building crypto-specific strategies',
        'Handling market volatility',
        'Position sizing for crypto',
        'Live trading considerations'
      ],
      homework: 'Create a crypto trading bot that runs 24/7 with proper risk controls',
      icon: <FaCubes />,
      color: '#10b981'
    },
    {
      week: 4,
      title: 'Week 4: Options & Futures',
      date: 'Week 4',
      description: 'Advanced derivatives trading strategies',
      topics: [
        'Options basics and Greeks',
        'Building options strategies (covered calls, spreads)',
        'Futures trading fundamentals',
        'Managing leverage and margin',
        'Advanced risk management'
      ],
      homework: 'Implement an options or futures strategy with proper position sizing',
      icon: <FaCubes />,
      color: '#8b5cf6'
    },
    {
      week: 5,
      title: 'Week 5: Deployment & Portfolio Management',
      date: 'Week 5',
      description: 'Going live and managing your trading portfolio',
      topics: [
        'Deploying bots to live trading',
        'Portfolio diversification strategies',
        'Monitoring and maintenance',
        'Performance analysis and optimization',
        'Next steps and community resources'
      ],
      homework: 'Deploy at least one strategy to paper trading and document your results',
      icon: <FaCubes />,
      color: '#f59e0b'
    }
  ];

  // Get curriculum data from a SINGLE cohort only
  // Priority: 1) Next future cohort, 2) Current cohort, 3) Most recent past cohort
  // Falls back to hardcoded curriculum if no API data available
  const getCurriculumWeeks = () => {
    if (!bootcampData?.sessionGroups) {
      console.log('No sessionGroups data, using fallback curriculum');
      return fallbackCurriculum;
    }

    const { past, current, future } = bootcampData.sessionGroups;
    let selectedSessions = [];

    // Priority 1: Next future cohort (hasn't started yet) - preferred for sales page
    if (future && Array.isArray(future) && future.length > 0) {
      // future array is sorted by start date, so first one is the next upcoming
      const nextFutureCohort = future[0];
      if (nextFutureCohort.sessions && Array.isArray(nextFutureCohort.sessions)) {
        selectedSessions = [...nextFutureCohort.sessions];
      }
    }

    // Priority 2: Current cohort (already started, but no future cohort available)
    if (selectedSessions.length === 0 && current && current.sessions && Array.isArray(current.sessions)) {
      selectedSessions = [...current.sessions];
    }

    // Priority 3: Most recent past cohort (fallback - rare case when no current/future)
    if (selectedSessions.length === 0 && past && Array.isArray(past) && past.length > 0) {
      // past array should have most recent last, so take the last one
      const mostRecentPast = past[past.length - 1];
      if (mostRecentPast.sessions && Array.isArray(mostRecentPast.sessions)) {
        selectedSessions = [...mostRecentPast.sessions];
      }
    }

    // If still no sessions found, use fallback curriculum
    if (selectedSessions.length === 0) {
      console.log('No sessions found in any cohort, using fallback curriculum');
      return fallbackCurriculum;
    }

    // Sort sessions by scheduled date within the selected cohort
    selectedSessions.sort((a, b) => {
      const dateA = new Date(a.scheduledAt || '2025-01-01');
      const dateB = new Date(b.scheduledAt || '2025-01-01');
      return dateA - dateB;
    });

    // Transform sessions to curriculum format
    const transformed = transformSessionsToCurriculum(selectedSessions);

    // Final fallback if transformation resulted in empty array
    return transformed.length > 0 ? transformed : fallbackCurriculum;
  };

  const curriculumWeeks = getCurriculumWeeks();

  // Debug logging to verify curriculum data
  console.log('BootcampCurriculum - bootcampData:', bootcampData);
  console.log('BootcampCurriculum - curriculumWeeks count:', curriculumWeeks.length);
  console.log('BootcampCurriculum - curriculumWeeks:', curriculumWeeks);

  const learningOutcomes = [
    {
      icon: <FaBrain />,
      title: 'AI Mastery',
      description: 'Transform natural language into sophisticated trading algorithms'
    },
    {
      icon: <FaLaptopCode />,
      title: 'Professional Development Tools',
      description: 'Master VS Code with Copilot and Cursor for advanced vibe coding techniques'
    },
    {
      icon: <FaCogs />,
      title: 'Multi-Asset Trading',
      description: 'Deploy bots across stocks, crypto, options, and futures markets'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Risk Management',
      description: 'Implement proper safeguards and position sizing strategies'
    },
    {
      icon: <FaUsers />,
      title: 'Community Access',
      description: 'Join 2,400+ traders and get ongoing support and collaboration'
    }
  ];

  return (
    <div className="bootcamp-curriculum">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="bootcamp-curriculum-header">
              <h2 className="bootcamp-curriculum-title">
                <FaGraduationCap className="me-3" />
                Curriculum Breakdown
              </h2>
              <p className="bootcamp-curriculum-subtitle">
                {curriculumWeeks.length} weeks of intensive, hands-on training that transforms beginners into confident algorithmic traders
              </p>
            </div>
          </Col>
        </Row>

        {/* Learning Outcomes */}
        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <div className="bootcamp-learning-outcomes">
              <h3 className="bootcamp-learning-title">What You'll Master</h3>
              <Row className="justify-content-center">
                {learningOutcomes.map((outcome, index) => (
                  <Col xl={2} lg={4} md={6} sm={6} key={index} className="mb-3 d-flex">
                    <div className="bootcamp-learning-item">
                      <div className="bootcamp-learning-icon">
                        {outcome.icon}
                      </div>
                      <h4 className="bootcamp-learning-item-title">{outcome.title}</h4>
                      <p className="bootcamp-learning-item-description">{outcome.description}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Col>
        </Row>

        {/* Weekly Curriculum */}
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="bootcamp-curriculum-weeks">
              <Accordion activeKey={activeKey} onSelect={setActiveKey}>
                {curriculumWeeks.map((week, index) => (
                  <Accordion.Item key={index} eventKey={index.toString()} className="bootcamp-curriculum-week-card">
                    <Accordion.Header className="bootcamp-curriculum-week-header">
                      <div className="bootcamp-curriculum-week-header-content">
                        <div className="bootcamp-curriculum-week-icon" style={{ borderColor: week.color }}>
                          {React.cloneElement(week.icon, { style: { color: week.color } })}
                        </div>
                        <div className="bootcamp-curriculum-week-info">
                          <div className="bootcamp-curriculum-week-number">
                            Week {week.week}
                          </div>
                          <div className="bootcamp-curriculum-week-date">
                            {week.date}
                          </div>
                          <h4 className="bootcamp-curriculum-week-title">
                            {week.title}
                          </h4>
                          <p className="bootcamp-curriculum-week-description">
                            {week.description}
                          </p>
                        </div>
                      </div>
                    </Accordion.Header>

                    <Accordion.Body className="bootcamp-curriculum-week-body">
                      <div className="bootcamp-curriculum-week-content">
                        <div className="bootcamp-curriculum-topics">
                          <h5 className="bootcamp-curriculum-section-title">
                            <FaList className="me-2" />
                            What We'll Cover
                          </h5>
                          <ul className="bootcamp-curriculum-topics-list">
                            {week.topics.map((topic, topicIndex) => (
                              <li key={topicIndex}>{topic}</li>
                            ))}
                          </ul>
                        </div>

                        {week.homework && (
                          <div className="bootcamp-curriculum-homework">
                            <h5 className="bootcamp-curriculum-section-title">
                              <FaTasks className="me-2" />
                              Homework Assignment
                            </h5>
                            <p className="bootcamp-curriculum-homework-text">
                              {week.homework}
                            </p>
                          </div>
                        )}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          </Col>
        </Row>

        {/* Bottom CTA */}
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="bootcamp-curriculum-cta">
              <div className="bootcamp-curriculum-cta-content">
                <h3 className="bootcamp-curriculum-cta-title">
                  Ready to Build Your AI Investing Workflow?
                </h3>
                <p className="bootcamp-curriculum-cta-description">
                  Join the next cohort and build a repeatable BotSpot workflow in {curriculumWeeks.length} weeks.
                </p>
                <div className="bootcamp-curriculum-cta-features">
                  <div className="bootcamp-curriculum-cta-feature">
                    <FaCheckCircle className="me-2" />
                    Live interactive sessions
                  </div>
                  <div className="bootcamp-curriculum-cta-feature">
                    <FaCheckCircle className="me-2" />
                    Lifetime access to recordings
                  </div>
                  <div className="bootcamp-curriculum-cta-feature">
                    <FaCheckCircle className="me-2" />
                    Pro community support
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BootcampCurriculum;
